import { Flex, Typography, useScreenSize } from '@banhala/ui-kit';
import Animated, {
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
  interpolate,
  Extrapolation,
  withTiming,
  useAnimatedReaction,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { ScrollContext } from '../context/ScrollContext';
import { useSetTab, useTabRef } from '../atom/tab.atom';
import { AnimatedList } from './AnimatedList';
import { SectionContainer } from './SectionContainer';
import { CoverImages } from './CoverImages';
import { MarketInfoBanner } from './MarketInfoBanner';
import { MumulLiveProfileSection } from './MumulLive/MumulLiveProfileSection';
import { GoodsInfo } from './GoodsInfo';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ExpectedPurchasePrice } from './ExpectedPurchasePrice';
import { GoodsPromotions } from './GoodsPromotions';
import { CouponListBanner } from './CouponListBanner';
import { DeliveryInfo } from './DeliveryInfo';
import { MarketInfo } from './MarketInfo';
import { BrandInfo } from './BrandInfo';
import type { GoodsDetailContentKeys } from '../types/GoodsDetailContentKeys';
import { useGoodsDetail } from '../queries/useGoodsDetail';
import { CategoryInfo } from './CategoryInfo';
import { ReviewSummary } from './ReviewSummary';
import { GoodsCarousel } from './GoodsCarousel';
import { ContentTab } from './ContentTab';
import type { GoodsDetailTab } from '../types/GoodsDetailTab';
import { GoodsContent } from './GoodsContent';
import { FloatingButtons } from './FloatingButtons';
import { BottomDockedButton } from './BottomDocked';
import { AffiliateFloatingButton } from './AffiliateFloating/AffiliateFloatingButton';
import { useAffiliateFloating } from '../hooks/useAffiliateFloating';
import { AiContentDraftFloatingButton } from './AiContentDraftFloating/AiContentDraftFloatingButton';
import { BottomDockedFloatingLayer } from './BottomDockedFloatingLayer';
import { useAiContentDraftFloating } from '../hooks/useAiContentDraftFloating';
import { useLogViewGoodsDetail } from '../hooks/useLogViewGoodsDetail';
import { useLogClickGoodsDetailTab } from '../hooks/useLogClickGoodsDetailTab';
import { LiveCampaignBanner } from './LiveCampaignBanner';
import { useRelatedLiveCampaign } from '../queries/useRelatedLiveCampaign';
import { useExperimentGroup } from 'shared/src/context/UserContext/useExperimentGroup';
import { useSafeTimeout } from 'shared/src/hooks';
import {
  retainedState,
  useRetainedEffect,
  useRetainedRef,
} from 'shared/src/hooks/retainedState';
import { useScrollRestoration } from 'shared/src/hooks/useScrollRestoration';
import { sectionHeightSlot } from '../store/sectionHeights';
import { useLogGoodsAnalyticsEvent } from '../hooks/useLogGoodsAnalyticsEvent';
import { useGoodsBasic } from '../queries/useGoodsBasic';
import { Metadata } from 'shared/src/component';
import {
  getMetaDescription,
  getMetaKeywords,
  getMetaTitle,
  getProductMeta,
} from '../utils/goodsMetaData';
import { isAndroid, isIOS, isNotNil, isWeb } from 'shared/src/utils';
import type { ListRenderItem } from 'react-native';
import { useGoodsSno } from '../hooks/useGoodsSno';
import { useGoodsDetailContentKeys } from '../hooks/useGoodsDetailContentKeys';
import type { FlatList } from 'react-native-gesture-handler';
import { useCheckGoodsRedirectDeeplink } from '../queries/useCheckGoodsRedirectDeeplink';

const LIVE_CAMPAIGN_BANNER_HEIGHT = 48;

const scrollOffsetSlot = retainedState(0);

const useLiveCampaignCGroupLogging = () => {
  const { data: campaign } = useRelatedLiveCampaign();
  const isCGroup = useExperimentGroup({
    sno: 'EXP1558',
    targetGroups: ['C'],
  });
  const logEvent = useLogGoodsAnalyticsEvent();

  useRetainedEffect(
    'view-shoplive-target-goods-detail',
    () => {
      if (!campaign || !isCGroup) return false;

      logEvent({
        name: 'VIEW_SHOPLIVE_TARGET_GOODS_DETAIL',
        channel: ['braze'],
        analytics: campaign.logging.analytics,
      });
    },
    [campaign, isCGroup, logEvent],
  );
};

export const GoodsDetailContents: React.FC = () => {
  const scrollY = useSharedValue(0);
  const isScrolling = useSharedValue(false);
  const goodsSno = useGoodsSno();

  const tabScrollX = useSharedValue(0);
  const { width, height: screenHeight } = useScreenSize();
  const setSafeTimeout = useSafeTimeout();
  const listRef = useRef<FlatList<GoodsDetailContentKeys> | null>(null);
  const tabLastScrollYValueRef = useRef<{
    [key in GoodsDetailTab]: number;
  }>({
    INFORMATION: 0,
    REVIEW: 0,
    SIZE: 0,
    INQUIRY: 0,
  });

  const sectionHeightRef = useRetainedRef(sectionHeightSlot);
  const stickyItemPositionY = useSharedValue(0);

  const recalcStickyPosition = useCallback(() => {
    const heights = sectionHeightRef.current;
    const nextY = Object.keys(heights)
      .filter(key => key !== 'content' && key !== 'content-tab')
      .reduce((prev, cur) => prev + heights[cur], 0);
    if (stickyItemPositionY.value !== nextY) {
      stickyItemPositionY.value = nextY;
    }
    // sectionHeightRef 는 useRetainedRef 가 useRef 로 메모한 안정 핸들이라 deps 에 넣어도
    // 콜백이 재생성되지 않는다(lint 는 useRef 결과만 자동 제외하므로 명시 필요).
  }, [stickyItemPositionY, sectionHeightRef]);

  const handleChangeHeight = useCallback(
    (key: string, height: number) => {
      if (sectionHeightRef.current[key] === height) return;
      sectionHeightRef.current[key] = height;
      recalcStickyPosition();
    },
    [recalcStickyPosition, sectionHeightRef],
  );

  const { data: goodsBasic } = useGoodsBasic();
  const { data: goodsDetail } = useGoodsDetail();

  const contentKeys = useGoodsDetailContentKeys();

  const { data: liveCampaign } = useRelatedLiveCampaign();
  const isBGroup = useExperimentGroup({
    sno: 'EXP1558',
    targetGroups: ['B'],
  });
  const showLiveBanner = isNotNil(liveCampaign) && isBGroup;

  const tabRef = useTabRef();
  const setTab = useSetTab();
  const logClickGoodsDetailTab = useLogClickGoodsDetailTab();

  useLiveCampaignCGroupLogging();
  useCheckGoodsRedirectDeeplink();

  const scrollToOffset = useCallback((offset: number, animated: boolean) => {
    listRef.current?.scrollToOffset({ offset, animated });
  }, []);

  const scrollRestore = useScrollRestoration({
    token: scrollOffsetSlot,
    scrollTo: scrollToOffset,
    viewportHeight: screenHeight,
  });

  const scrollHandler = useAnimatedScrollHandler({
    onScroll: event => {
      scrollY.value = event.contentOffset.y;
      isScrolling.value = true;
    },
    onEndDrag: event => {
      // 손가락을 뗐는데 속도가 거의 없으면(=관성 없음) 즉시 정지로 본다.
      // 속도가 있으면 onMomentum*로 이어지므로 여기서 false 처리하지 않는다.
      if (Math.abs(event.velocity?.y ?? 0) < 0.1) {
        isScrolling.value = false;
        scheduleOnRN(scrollRestore.save, scrollY.value);
      }
    },
    onMomentumBegin: () => {
      isScrolling.value = true;
    },
    onMomentumEnd: () => {
      isScrolling.value = false;
      scheduleOnRN(scrollRestore.save, scrollY.value);
    },
  });

  // sticky overlay 활성 상태 — pointerEvents 제어용.
  // useAnimatedReaction 으로 threshold 교차 시점에만 setState (1프레임 단위 X)
  const [isStickyActive, setIsStickyActive] = useState(false);
  useAnimatedReaction(
    () =>
      stickyItemPositionY.value > 0 &&
      scrollY.value >= stickyItemPositionY.value,
    (current, previous) => {
      if (current !== previous) {
        scheduleOnRN(setIsStickyActive, current);
      }
    },
    [scrollY, stickyItemPositionY],
  );

  const stickyOverlayStyle = useAnimatedStyle(() => {
    const active =
      stickyItemPositionY.value > 0 &&
      scrollY.value >= stickyItemPositionY.value;
    return {
      opacity: active ? 1 : 0,
    };
  });

  const scrollToStickyView = useCallback(() => {
    listRef.current?.scrollToOffset({
      offset: stickyItemPositionY.value,
      animated: true,
    });
  }, [stickyItemPositionY]);

  const changeContentTab = useCallback(
    (nextTab: GoodsDetailTab) => {
      setTab(nextTab);
      const tabList: GoodsDetailTab[] = [
        'INFORMATION',
        'REVIEW',
        'SIZE',
        'INQUIRY',
      ];
      if (isIOS) {
        tabScrollX.value = withTiming(
          tabList.findIndex(t => t === nextTab),
          { duration: 200 },
        );
      }
    },
    [setTab, tabScrollX],
  );

  const onChangeTab = useCallback(
    (nextTab: GoodsDetailTab) => {
      const prevTab = tabRef.current;
      if (prevTab !== nextTab) {
        logClickGoodsDetailTab(nextTab);
        const currentScrollY = scrollY.value;
        const sticky = stickyItemPositionY.value;
        // ① 나가는 탭의 마지막 스크롤 저장
        tabLastScrollYValueRef.current = {
          ...tabLastScrollYValueRef.current,
          [prevTab]: currentScrollY,
        };

        // Android 의 프레임 재시도 복원(restoreTabScroll)은 의도와 다른 위치로 스크롤시켜
        // 부자연스러워 제거했다 → Android 는 탭 전환 시 강제 스크롤 없음(현재 위치 유지).
        // iOS 는 기존 동작(setTimeout 복원)을 유지. onChangeTab 은 Android 스와이프
        // (GoodsContent.tsx)와 공유 sticky 탭바(ContentTab) 양쪽에서 호출된다.
        if (!isAndroid && currentScrollY > sticky) {
          setSafeTimeout(() => {
            scrollToOffset(
              Math.max(tabLastScrollYValueRef.current[nextTab], sticky),
              false,
            );
          }, 1);
        }
      }
      setTab(nextTab);
    },
    [
      tabRef,
      setTab,
      logClickGoodsDetailTab,
      scrollY,
      stickyItemPositionY,
      setSafeTimeout,
      scrollToOffset,
    ],
  );

  useLogViewGoodsDetail();

  const { floatingText, showFloating, onViewFloating, onClickFloating } =
    useAffiliateFloating();

  const {
    showFloating: showAiContentFloating,
    onClickFloating: onClickAiContentFloating,
    onCloseFloating: onCloseAiContentFloating,
  } = useAiContentDraftFloating();

  const listStyle = useMemo(() => ({ flex: 1 }), []);

  const listContentContainerStyle = useMemo(
    () =>
      showLiveBanner ? { paddingTop: LIVE_CAMPAIGN_BANNER_HEIGHT } : undefined,
    [showLiveBanner],
  );

  const renderItem = useCallback<ListRenderItem<GoodsDetailContentKeys>>(
    ({ item }) => {
      if (item === 'cover') {
        return (
          <SectionContainer
            height={width}
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <CoverImages />
          </SectionContainer>
        );
      }

      if (item === 'promotions') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <GoodsPromotions />
          </SectionContainer>
        );
      }

      if (item === 'market') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <MarketInfoBanner />
          </SectionContainer>
        );
      }

      if (item === 'info') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            {/* warm 서피스 재진입은 mount가 유지되므로, 상품이 바뀌면 key로 새로
            mount해 진입당 1회 상태(가격 롤링·노출 로깅)를 초기화한다. */}
            <GoodsInfo key={goodsSno.toString()} />
          </SectionContainer>
        );
      }

      if (item === 'expected-purchase-price') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <ExpectedPurchasePrice />
          </SectionContainer>
        );
      }

      if (item === 'coupons') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <CouponListBanner />
          </SectionContainer>
        );
      }

      if (item === 'delivery-info') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <DeliveryInfo />
          </SectionContainer>
        );
      }

      if (item === 'mumul-live-profile') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <MumulLiveProfileSection />
          </SectionContainer>
        );
      }

      if (item === 'market-info') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <MarketInfo />
          </SectionContainer>
        );
      }
      if (item === 'brand-info') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <BrandInfo />
          </SectionContainer>
        );
      }
      if (item === 'review-summary') {
        if (!goodsDetail?.component_visibility.review_summary) return null;
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <ReviewSummary />
          </SectionContainer>
        );
      }

      if (item === 'category-info') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <CategoryInfo />
          </SectionContainer>
        );
      }

      if (item === 'carousel') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <GoodsCarousel />
          </SectionContainer>
        );
      }

      if (item === 'content-tab') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <ContentTab />
          </SectionContainer>
        );
      }

      if (item === 'content') {
        // if (!isAndroid && (isNil(goodsBasic) || isNil(goodsDetail))) {
        //   return null;
        // }
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <GoodsContent />
          </SectionContainer>
        );
      }

      return (
        <Flex height={300} backgroundColor={'white'}>
          <Typography>{item}</Typography>
        </Flex>
      );
    },
    [width, handleChangeHeight, goodsDetail, goodsSno],
  );

  const bannerAnimatedStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateY: interpolate(
            scrollY.value,
            [0, LIVE_CAMPAIGN_BANNER_HEIGHT],
            [0, -LIVE_CAMPAIGN_BANNER_HEIGHT],
            Extrapolation.CLAMP,
          ),
        },
      ],
    };
  });

  const scrollContextValue = useMemo(
    () => ({
      listRef,
      scrollY,
      isScrolling,
      tabScrollX,
      scrollToStickyView,
      changeContentTab,
      onChangeTab,
    }),
    [
      scrollToStickyView,
      changeContentTab,
      onChangeTab,
      listRef,
      scrollY,
      isScrolling,
      tabScrollX,
    ],
  );

  const stickyOverlayContainerStyle = useMemo(
    () => ({
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
    }),
    [],
  );

  return (
    <ScrollContext value={scrollContextValue}>
      {isNotNil(goodsBasic) && (
        <Metadata
          title={getMetaTitle(goodsBasic)}
          imgSrc={goodsBasic?.cover_images[0] ?? ''}
          path={`/goods/${goodsBasic.sno}`}
          description={getMetaDescription(goodsBasic)}
          keywords={getMetaKeywords(goodsBasic)}
          productMeta={getProductMeta(goodsBasic)}
          isCanonical
        />
      )}
      {showLiveBanner && (
        <Animated.View
          style={[
            {
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1,
            },
            bannerAnimatedStyle,
          ]}
        >
          <LiveCampaignBanner />
        </Animated.View>
      )}
      <AnimatedList
        ref={listRef}
        {...(isWeb ? { nativeID: 'goods-detail-scroll' } : {})}
        style={[listStyle, scrollRestore.restoring ? { opacity: 0 } : null]}
        contentContainerStyle={[
          listContentContainerStyle,
          isWeb && { maxWidth: width, alignSelf: 'center' },
        ]}
        overScrollMode={isAndroid ? 'never' : undefined}
        windowSize={isAndroid ? 5 : undefined}
        // 첫 콘텐츠 커밋을 above-fold(커버/가격 등)만으로 작게 유지 → push 후 커버가 빨리
        // 페인트되고, 나머지 섹션은 배치로 스트리밍. (기본 initialNumToRender=10 은 첫 커밋에
        // 하위 섹션까지 몰아 그려 커버 표시가 늦어졌다.)
        initialNumToRender={4}
        maxToRenderPerBatch={4}
        onScroll={scrollHandler}
        onContentSizeChange={scrollRestore.onContentSizeChange}
        contentInsetAdjustmentBehavior={'never'}
        data={contentKeys}
        renderItem={renderItem}
        scrollEventThrottle={1}
      />
      {/* Sticky overlay — content-tab의 자연 위치를 지나면 fade in.
          RN의 stickyHeaderIndices를 쓰지 않고 자체 구현하여
          ScrollViewStickyHeader의 cascading setState 회피. */}
      <Animated.View
        pointerEvents={isStickyActive ? 'auto' : 'none'}
        style={[stickyOverlayContainerStyle, stickyOverlayStyle]}
      >
        <ContentTab />
      </Animated.View>
      <Flex justifyContent={'center'} alignItems={'center'}>
        <BottomDockedFloatingLayer>
          <FloatingButtons
            scrollToYPosition={isAndroid ? stickyItemPositionY.value : 0}
          />
          {!showAiContentFloating && showFloating && (
            <AffiliateFloatingButton
              text={floatingText}
              onPress={onClickFloating}
              onView={onViewFloating}
            />
          )}
          {showAiContentFloating && (
            <AiContentDraftFloatingButton
              onPress={onClickAiContentFloating}
              onClose={onCloseAiContentFloating}
            />
          )}
        </BottomDockedFloatingLayer>
        <Flex width={width}>
          {/* web에서 해당 key값을 할당 하지 않으면 unmount가 되지 않고 단순히 re-render가됨.
          이전 상품에서 선택했던 옵션을 그대로 보여줍니다.
          key값을 추가해서 새로 받아 올 수 있도록 합니다.
          */}
          <BottomDockedButton key={goodsSno.toString()} />
        </Flex>
      </Flex>
    </ScrollContext>
  );
};
